/**
 * TonightTab — quiet stewardship of a living gathering.
 *
 * What this is:
 *   A low-information observational surface that surfaces ONLY while
 *   an event is in the "gathering" or "returning" state. The host
 *   does not come here to monitor — they come here to feel that the
 *   night is being held.
 *
 * What this is NOT (constitutionally banned):
 *   • upload counts
 *   • live graphs / sparklines
 *   • participant counters
 *   • device health
 *   • throughput / latency / bandwidth
 *   • activity feeds with timestamps
 *   • "control center" framing
 *
 * Every signal is derived from data we already preserve for other
 * surfaces — no new endpoints, no live sockets, no metrics. A 45s
 * soft poll is the upper bound of liveness; that is intentional.
 *
 * Each observation has two registers:
 *   • PRESENT   — full ink, italic, calm
 *   • DORMANT   — faint ink, the same words, no animation
 * The host reads the room like a poem, not a dashboard.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  MODE_TWO,
  Eyebrow, EditorialHeading, GraphitePanel, MutedDivider,
  StateGlyph, deriveEventState,
} from './ModeTwoPrimitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Observation windows ───────────────────────────────────────────
// Each window is a deliberately rounded human interval, not an
// engineering threshold. We want "the last quarter-hour", not
// "the last 900 seconds".
const KIOSK_AWAKE_WINDOW_MS  = 30 * 60 * 1000;
const ROOM_GATHERING_WINDOW_MS = 15 * 60 * 1000;
const REVISIT_WINDOW_MS = 24 * 60 * 60 * 1000;

const POLL_INTERVAL_MS = 45 * 1000;
const RENDER_TICK_MS   = 30 * 1000;

// Parse a server-supplied ISO/datetime string to a millisecond ms
// epoch, returning null on any failure. Pure, no throws.
function parseTime(value) {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

// Pick the latest `created_at`-shaped timestamp from a list of docs.
function latestTimestamp(docs, field = 'created_at') {
  if (!Array.isArray(docs) || docs.length === 0) return null;
  let best = null;
  for (const d of docs) {
    // Photos serialise `uploaded_at` while memory-snaps and
    // guestbook entries serialise `created_at`. Honour whichever
    // the document supplies so the observation fires on either path.
    const t = parseTime(d?.[field]) || parseTime(d?.uploaded_at) || parseTime(d?.created_at);
    if (t != null && (best == null || t > best)) best = t;
  }
  return best;
}

// ─────────────────────────────────────────────────────────────────
//  Observation atom
// ─────────────────────────────────────────────────────────────────
// A single italic line of room-sense, one calm ✦ glyph in front.
// Present and dormant share identical typography; only ink shifts.
const Observation = ({ active, children, testId }) => (
  <div
    data-testid={testId}
    data-state={active ? 'present' : 'dormant'}
    style={{
      display: 'flex', alignItems: 'baseline', gap: 14,
      padding: '18px 4px',
      fontFamily: MODE_TWO.serif, fontStyle: 'italic',
      fontSize: 'clamp(15px, 1.6vw, 17px)',
      lineHeight: 1.5,
      color: active ? MODE_TWO.ink : MODE_TWO.inkFaint,
      letterSpacing: '0.005em',
      transition: 'color 600ms ease',
    }}
  >
    <span
      aria-hidden
      style={{
        fontSize: 12,
        color: active ? MODE_TWO.gold : MODE_TWO.inkFaint,
        transition: 'color 600ms ease',
        flexShrink: 0,
      }}
    >✦</span>
    <span>{children}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────
//  TonightTab — the room itself
// ─────────────────────────────────────────────────────────────────
const TonightTab = ({ event }) => {
  const [pairing, setPairing] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [snaps, setSnaps] = useState([]);
  const [, setRenderTick] = useState(0);
  const mountedRef = useRef(true);

  // Soft poll: fetch the three observational sources in parallel,
  // tolerate failures silently. We never surface error states — a
  // dropped fetch simply leaves the observation in its previous
  // register until the next tick.
  useEffect(() => {
    mountedRef.current = true;
    const fetchAll = async () => {
      try {
        const [p, ph, sn] = await Promise.allSettled([
          axios.get(`${API}/events/${event.event_id}/booth-pairing`),
          axios.get(`${API}/events/${event.event_id}/photos`),
          axios.get(`${API}/events/${event.event_id}/memory-snaps`),
        ]);
        if (!mountedRef.current) return;
        if (p.status === 'fulfilled') setPairing(p.value.data || null);
        if (ph.status === 'fulfilled') {
          const data = ph.value.data;
          setPhotos(Array.isArray(data) ? data : (data?.photos || []));
        }
        if (sn.status === 'fulfilled') {
          const data = sn.value.data;
          setSnaps(Array.isArray(data) ? data : (data?.snaps || data?.memory_snaps || []));
        }
      } catch (_) { /* silent — observational, not operational */ }
    };
    fetchAll();
    // Tick wrapper — skip when the host has moved to another tab.
    // The page still fires intervals while hidden but no one's
    // looking; we resume on visibilitychange→visible with a single
    // fresh fetch so the surface is immediately accurate when the
    // host returns to it.
    const tickIfVisible = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchAll();
    };
    const pollId = setInterval(tickIfVisible, POLL_INTERVAL_MS);
    // Independent render tick so each observation can lapse from
    // PRESENT → DORMANT without waiting for the next poll.
    const tickId = setInterval(() => {
      if (mountedRef.current && !document.hidden) setRenderTick((n) => n + 1);
    }, RENDER_TICK_MS);
    const onVis = () => {
      if (!document.hidden && mountedRef.current) fetchAll();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      mountedRef.current = false;
      clearInterval(pollId);
      clearInterval(tickId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [event.event_id]);

  const state = deriveEventState(event);

  // ── Derived observations ────────────────────────────────────────
  const now = Date.now();

  // 1) Kiosk awake — the same 30m window the Setup panel uses, so
  //    the two surfaces never disagree.
  const kioskLastUsed = parseTime(pairing?.last_used_at);
  const kioskAwake = !!(pairing?.code && kioskLastUsed && (now - kioskLastUsed) < KIOSK_AWAKE_WINDOW_MS);

  // 2) The room is gathering — anything new (photo OR snap) in the
  //    last 15 minutes. Pure presence: no count is ever surfaced.
  const lastPhotoAt = latestTimestamp(photos);
  const lastSnapAt  = latestTimestamp(snaps);
  const lastAnyAt   = Math.max(lastPhotoAt || 0, lastSnapAt || 0) || null;
  const roomGathering = !!(lastAnyAt && (now - lastAnyAt) < ROOM_GATHERING_WINDOW_MS);

  // 3) Memories settling onto the wall — true whenever ANY memory
  //    exists for tonight. Dormant before the first contribution.
  const memoriesPresent = (photos.length + snaps.length) > 0;

  // 4) Guests beginning to revisit — only meaningful in "returning".
  //    During gathering we keep this observation dormant; during
  //    returning we set it present if there has been any memory
  //    activity in the last 24h (i.e. the night is still warm).
  const revisiting = state === 'returning' && !!(lastAnyAt && (now - lastAnyAt) < REVISIT_WINDOW_MS);

  // Headline that frames the room without describing it. Different
  // verb in each state; never tells the host what to do.
  const headline = state === 'returning'
    ? 'The night is still warm.'
    : 'The room is being held.';

  // Eyebrow tone — matches state register, intentionally calm.
  const eyebrow = useMemo(() => state === 'returning' ? '✦ STILL RETURNING' : '✦ TONIGHT', [state]);

  return (
    <div data-testid="tonight-tab-panel">
      <GraphitePanel
        testId="tonight-header"
        padding="clamp(24px, 3vw, 36px) clamp(22px, 2.6vw, 32px)"
        style={{ marginBottom: 16 }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12,
        }}>
          <StateGlyph state={state} size={10} testId="tonight-state-glyph" />
          <Eyebrow>{eyebrow}</Eyebrow>
        </div>
        <EditorialHeading size="h1">
          <span data-testid="tonight-headline">{headline}</span>
        </EditorialHeading>
        <div style={{
          marginTop: 10, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 13.5, color: MODE_TWO.inkMuted, lineHeight: 1.55,
          maxWidth: 560,
        }}>
          {state === 'returning'
            ? 'A few last memories are still settling in. Step back into the room and let the night close on its own.'
            : 'Nothing here needs your attention. This is just the night, observed.'}
        </div>
      </GraphitePanel>

      <GraphitePanel
        testId="tonight-observations"
        padding="clamp(20px, 2.4vw, 30px) clamp(22px, 2.6vw, 32px)"
      >
        <div style={{
          fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 10.5, letterSpacing: '0.26em',
          textTransform: 'uppercase', color: MODE_TWO.goldSoft,
          marginBottom: 10,
        }}>✦ FROM THE ROOM</div>
        <MutedDivider />

        <Observation active={kioskAwake} testId="tonight-obs-kiosk-awake">
          A kiosk is awake
        </Observation>
        <MutedDivider />

        <Observation active={roomGathering} testId="tonight-obs-room-gathering">
          The room is gathering
        </Observation>
        <MutedDivider />

        <Observation active={memoriesPresent} testId="tonight-obs-wall-settling">
          Memories are settling onto the wall
        </Observation>
        <MutedDivider />

        <Observation active={revisiting} testId="tonight-obs-revisiting">
          Guests are beginning to revisit the night
        </Observation>
      </GraphitePanel>
    </div>
  );
};

export default TonightTab;
